<script setup lang="ts">
import {
  ArrowRight,
  ChartNoAxesCombined,
  ShieldCheck,
  UserRoundPlus,
} from "lucide-vue-next";

const localeRoute = useLocaleRoute();
const { t } = useI18n();

const benefitCopy = computed(() => [
  {
    icon: UserRoundPlus,
    title: t("professionals_cta.benefits.customers.title"),
    description: t("professionals_cta.benefits.customers.description"),
  },
  {
    icon: ChartNoAxesCombined,
    title: t("professionals_cta.benefits.growth.title"),
    description: t("professionals_cta.benefits.growth.description"),
  },
  {
    icon: ShieldCheck,
    title: t("professionals_cta.benefits.control.title"),
    description: t("professionals_cta.benefits.control.description"),
  },
]);
</script>

<template>
  <section class="w-full bg-white py-12 md:py-16">
    <div class="mx-auto w-full max-w-[1280px] px-4 md:px-6">
      <div
        class="relative overflow-hidden rounded-2xl bg-primary px-6 py-7 text-white md:px-9 md:py-9 lg:px-10"
      >
        <div
          class="pointer-events-none absolute -right-24 -top-36 h-80 w-80 rounded-full border border-white/[0.08]"
        />
        <div
          class="pointer-events-none absolute -bottom-48 right-[26%] h-80 w-80 rounded-full border border-white/[0.06]"
        />

        <div
          class="relative grid items-center gap-8 lg:grid-cols-[minmax(0,1fr)_600px] lg:gap-8"
        >
          <div>
            <p
              class="text-xs font-semibold uppercase tracking-[0.08em] text-white/70"
            >
              {{ $t("professionals_cta.label") }}
            </p>

            <h2
              class="mt-2 max-w-[590px] font-gabarito text-3xl font-bold leading-[1.06] md:text-[36px]"
            >
              {{ $t("professionals_cta.title") }}
            </h2>

            <p class="mt-3 max-w-[610px] text-[15px] leading-6 text-white/90">
              {{ $t("professionals_cta.description") }}
            </p>

            <div class="mt-6 flex flex-col gap-3 sm:flex-row">
              <NuxtLink
                :to="localeRoute('/register')"
                class="inline-flex min-h-12 items-center justify-center rounded-lg bg-white px-6 text-sm font-semibold text-primary transition duration-300 hover:bg-primary-50"
              >
                {{ $t("professionals_cta.actions.register") }}
              </NuxtLink>

              <NuxtLink
                :to="localeRoute('/for-professionals')"
                class="group inline-flex min-h-12 items-center justify-center gap-2 rounded-lg border border-white/35 px-6 text-sm font-semibold text-white transition duration-300 hover:border-white/60 hover:bg-white/[0.08]"
              >
                {{ $t("professionals_cta.actions.learn_more") }}
                <ArrowRight
                  class="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5"
                />
              </NuxtLink>
            </div>
          </div>

          <div class="hidden sm:grid sm:grid-cols-3">
            <div
              v-for="(benefit, index) in benefitCopy"
              :key="benefit.title"
              class="flex min-h-[168px] flex-col items-center justify-center px-5 py-5 text-center"
              :class="
                index > 0
                  ? 'border-t border-white/[0.10] sm:border-l sm:border-t-0'
                  : ''
              "
            >
              <component
                :is="benefit.icon"
                class="h-8 w-8 text-white/90"
                :stroke-width="1.55"
              />

              <p class="mt-3 font-gabarito text-[17px] font-bold text-white">
                {{ benefit.title }}
              </p>

              <p class="mt-2 max-w-[155px] text-[13px] leading-5 text-white/75">
                {{ benefit.description }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
